<template>
  <div class="page-container">
    <el-tabs v-model="activeTab">
      <!-- ================= 巡检任务 ================= -->
      <el-tab-pane label="巡检任务" name="record">
        <div class="toolbar">
          <div class="actions">
            <el-button type="primary" @click="openRecordModal">新建任务</el-button>
          </div>
          <div class="filters">
            <el-select v-model="query.grainStoreId" placeholder="全部粮库" clearable style="width: 160px">
              <el-option v-for="s in storeOptions" :key="s.id" :label="s.storeName" :value="s.id" />
            </el-select>
            <el-select v-model="query.taskStatus" placeholder="任务状态" clearable style="width: 140px">
              <el-option label="未巡检" :value="0" />
              <el-option label="巡检中" :value="1" />
              <el-option label="已巡检" :value="2" />
              <el-option label="已过期" value="__expired__" />
            </el-select>
            <el-select v-model="query.result" placeholder="巡检结果" clearable style="width: 140px">
              <el-option label="待确认" :value="2" />
              <el-option label="正常" :value="1" />
              <el-option label="异常" :value="0" />
            </el-select>
            <el-button type="primary" @click="loadRecords">查询</el-button>
            <el-button @click="resetQuery">重置</el-button>
          </div>
        </div>

        <el-table stripe :data="patrolRecords" border>
          <el-table-column prop="recordNo" label="任务编号" width="180" />
          <el-table-column label="所属粮库" width="160">
            <template #default="scope">{{ storeMap[scope.row.grainStoreId] || '-' }}</template>
          </el-table-column>
          <el-table-column prop="content" label="巡检要求" min-width="220">
            <template #default="scope">
              <el-tooltip placement="top" :show-after="200">
                <template #content>
                  <div style="white-space: pre-wrap; word-break: break-all; line-height: 1.6; max-width: 420px">{{ scope.row.content }}</div>
                </template>
                <span style="display: inline-block; max-width: 200px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; cursor: default">{{ scope.row.content }}</span>
              </el-tooltip>
            </template>
          </el-table-column>
          <el-table-column label="任务状态" width="140">
            <template #default="scope">
              <el-tag v-if="isExpired(scope.row)" type="danger" size="small">已过期</el-tag>
              <el-tag v-if="Number(scope.row.taskStatus) === 0" type="info" size="small">未巡检</el-tag>
              <el-tag v-else-if="Number(scope.row.taskStatus) === 1" type="warning" size="small">巡检中</el-tag>
              <el-tag v-else-if="Number(scope.row.taskStatus) === 2" type="success" size="small">已巡检</el-tag>
              <el-tag v-else type="info" size="small">未巡检</el-tag>
            </template>
          </el-table-column>
          <el-table-column label="巡检结果" width="100">
            <template #default="scope">
              <el-tag v-if="Number(scope.row.result) === 0" type="danger" size="small">异常</el-tag>
              <el-tag v-else-if="Number(scope.row.result) === 1" type="success" size="small">正常</el-tag>
              <el-tag v-else type="info" size="small">待确认</el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="operator" label="巡检人员" width="140" show-overflow-tooltip />
          <el-table-column prop="createTime" label="生成时间" width="170">
            <template #default="scope">{{ formatDateTime(scope.row.createTime) }}</template>
          </el-table-column>
          <el-table-column label="操作" width="200" fixed="right">
            <template #default="scope">
              <el-button size="small" type="primary" link @click="openProcess(scope.row)">巡检过程</el-button>
              <el-button size="small" link @click="editRecord(scope.row)">编辑</el-button>
              <el-button size="small" type="danger" link @click="deleteRecord(scope.row.id)">删除</el-button>
            </template>
          </el-table-column>
        </el-table>

        <MyPager :total="total" v-model:page="pageNum" v-model:size="pageSize" @change="loadRecords" />
      </el-tab-pane>

      <!-- ================= 巡检计划 ================= -->
      <el-tab-pane label="巡检计划" name="plan">
        <div class="toolbar">
          <div class="actions">
            <el-button type="primary" @click="openPlanModal">新建计划</el-button>
          </div>
          <div class="filters"></div>
        </div>
        <el-table stripe :data="patrolPlans" border>
          <el-table-column prop="planName" label="计划名称" min-width="180" />
          <el-table-column label="覆盖粮库" min-width="240">
            <template #default="scope">
              <el-tag v-for="s in parseStoreNames(scope.row.grainStoreIds)" :key="s" size="small" style="margin:2px 4px 2px 0">{{ s }}</el-tag>
              <span v-if="!parseStoreNames(scope.row.grainStoreIds).length" class="lk-text-muted">-</span>
            </template>
          </el-table-column>
          <el-table-column label="周期" width="180">
            <template #default="scope">
              <template v-if="scope.row.cycleType === 'once'">{{ formatDate(scope.row.planTime) }} {{ scope.row.executeTime || '' }}</template>
              <template v-else-if="scope.row.cycleType === 'daily'">每天 {{ scope.row.executeTime || '' }}</template>
              <template v-else-if="scope.row.cycleType === 'weekly'">每周 {{ weekLabel(scope.row.cycleValue) }} {{ scope.row.executeTime || '' }}</template>
              <template v-else-if="scope.row.cycleType === 'monthly'">每月 {{ scope.row.cycleValue }} 号 {{ scope.row.executeTime || '' }}</template>
              <template v-else>{{ scope.row.executeTime || formatDate(scope.row.planTime) }}</template>
            </template>
          </el-table-column>
          <el-table-column prop="description" label="巡检要求" min-width="200">
            <template #default="scope">
              <el-tooltip placement="top" :show-after="200">
                <template #content>
                  <div style="white-space: pre-wrap; word-break: break-all; line-height: 1.6; max-width: 420px">{{ scope.row.description }}</div>
                </template>
                <span style="display: inline-block; max-width: 240px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; cursor: default">{{ scope.row.description }}</span>
              </el-tooltip>
            </template>
          </el-table-column>
          <el-table-column prop="status" label="状态" width="100">
            <template #default="scope">
              <el-tag :type="planStatusTag(scope.row.status)">{{ getPlanStatusName(scope.row.status) }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="createTime" label="创建时间" width="170">
            <template #default="scope">{{ formatDateTime(scope.row.createTime) }}</template>
          </el-table-column>
          <el-table-column label="操作" width="160" fixed="right">
            <template #default="scope">
              <el-button size="small" @click="editPlan(scope.row)">编辑</el-button>
              <el-button size="small" type="danger" @click="deletePlan(scope.row.id)">删除</el-button>
            </template>
          </el-table-column>
        </el-table>
      </el-tab-pane>
    </el-tabs>

    <!-- ================= 巡检计划弹窗 ================= -->
    <el-dialog title="巡检计划" v-model="planDialogVisible" width="620px">
      <el-form :model="planForm" label-width="100px">
        <el-form-item label="计划名称" required>
          <el-input v-model="planForm.planName" placeholder="如：每周五下午巡检所有站" />
        </el-form-item>
        <el-form-item label="覆盖粮库" required>
          <el-select v-model="planForm.grainStoreIdsArr" multiple collapse-tags collapse-tags-tooltip
                     placeholder="请选择一个或多个粮库" filterable style="width:100%">
            <el-option v-for="s in storeOptions" :key="s.id" :label="s.storeName" :value="s.id" />
          </el-select>
        </el-form-item>
        <el-form-item label="周期类型" required>
          <el-radio-group v-model="planForm.cycleType">
            <el-radio value="once">单次</el-radio>
            <el-radio value="daily">每天</el-radio>
            <el-radio value="weekly">每周</el-radio>
            <el-radio value="monthly">每月</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item v-if="planForm.cycleType === 'once'" label="执行日期" required>
          <el-date-picker v-model="planForm.planTime" type="date" placeholder="选择执行日期" value-format="YYYY-MM-DD" style="width:100%" />
        </el-form-item>
        <el-form-item v-if="planForm.cycleType === 'weekly'" label="每周几" required>
          <el-checkbox-group v-model="weeklyDays">
            <el-checkbox v-for="d in weekDays" :key="d.v" :value="d.v">{{ d.t }}</el-checkbox>
          </el-checkbox-group>
        </el-form-item>
        <el-form-item v-if="planForm.cycleType === 'monthly'" label="每月几号" required>
          <el-checkbox-group v-model="monthlyDays">
            <el-checkbox v-for="n in monthNumbers" :key="n" :value="n">{{ n }}</el-checkbox>
          </el-checkbox-group>
        </el-form-item>
        <el-form-item label="执行时间" required>
          <el-select v-model="planForm.executeTime" placeholder="选择执行时间" style="width:100%" filterable>
            <el-option v-for="t in halfHourTimes" :key="t" :label="t" :value="t" />
          </el-select>
        </el-form-item>
        <el-form-item label="巡检要求">
          <el-input v-model="planForm.description" type="textarea" :rows="4"
                    placeholder="该计划需要检查的内容、注意事项、操作标准等&#10;如：1. 检查粮面温湿度是否正常&#10;2. 检查通风设施是否完好&#10;3. 检查是否有鼠雀痕迹" />
        </el-form-item>
        <el-form-item label="状态">
          <el-select v-model="planForm.status">
            <el-option label="未启用" :value="0" />
            <el-option label="执行中" :value="1" />
            <el-option label="已完成" :value="2" />
          </el-select>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="planDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSavePlan">确定</el-button>
      </template>
    </el-dialog>

    <!-- ================= 巡检任务弹窗（新建/编辑） ================= -->
    <el-dialog title="巡检任务" v-model="recordDialogVisible" width="560px">
      <el-form :model="recordForm" label-width="100px">
        <el-form-item label="任务编号" required>
          <el-input v-model="recordForm.recordNo" placeholder="系统自动生成可手动改" :disabled="!!recordForm.id" />
        </el-form-item>
        <el-form-item label="所属粮库" required>
          <el-select v-model="recordForm.grainStoreId" placeholder="请选择粮库" filterable style="width:100%"
                     @change="onStoreChange">
            <el-option v-for="s in storeOptions" :key="s.id" :label="s.storeName" :value="s.id" />
          </el-select>
        </el-form-item>
        <el-form-item label="巡检要求" required>
          <el-input v-model="recordForm.content" type="textarea" :rows="4" placeholder="请输入巡检任务要求" />
        </el-form-item>
        <el-form-item label="巡检人员">
          <el-input v-model="recordForm.operator" disabled placeholder="从粮库配置自动获取巡检员" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="recordDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSaveRecord">确定</el-button>
      </template>
    </el-dialog>

    <!-- ================= 巡检过程弹窗（和粮库管理同风格的覆盖层） ================= -->
    <transition name="manage-fade">
      <div v-if="processDialogVisible" class="proc-mask" @click.self="processDialogVisible = false">
        <div class="proc-panel">
          <div class="proc-title">
            <span>巡检任务详情 · {{ processRecord.recordNo || '' }}</span>
            <el-button text circle @click="processDialogVisible = false">
              <el-icon :size="18"><Close /></el-icon>
            </el-button>
          </div>
          <el-tabs v-model="activeProcessTab" type="card" class="proc-tabs">
            <!-- tab 1: 任务详情 -->
            <el-tab-pane name="detail">
              <template #label>
                <span style="display:inline-flex; align-items:center; gap:6px">📋 任务详情</span>
              </template>
              <div style="padding:20px 24px">
                <el-descriptions :column="3" border size="default">
                  <el-descriptions-item label="任务编号">
                    <b>{{ processRecord.recordNo }}</b>
                  </el-descriptions-item>
                  <el-descriptions-item label="所属粮库">
                    {{ storeMap[processRecord.grainStoreId] || '-' }}
                  </el-descriptions-item>
                  <el-descriptions-item label="生成时间">
                    {{ formatDateTime(processRecord.createTime) }}
                  </el-descriptions-item>
                  <el-descriptions-item label="任务状态">
                    <el-tag v-if="Number(processRecord.taskStatus) === 0" type="info" size="small">未巡检</el-tag>
                    <el-tag v-else-if="Number(processRecord.taskStatus) === 1" type="warning" size="small">巡检中</el-tag>
                    <el-tag v-else-if="Number(processRecord.taskStatus) === 2" type="success" size="small">已巡检</el-tag>
                  </el-descriptions-item>
                  <el-descriptions-item label="巡检结果">
                    <el-tag v-if="Number(processRecord.result) === 0" type="danger" size="small">异常</el-tag>
                    <el-tag v-else-if="Number(processRecord.result) === 1" type="success" size="small">正常</el-tag>
                    <el-tag v-else type="info" size="small">待确认</el-tag>
                  </el-descriptions-item>
                  <el-descriptions-item label="巡检人员">
                    {{ processRecord.operator || '-' }}
                  </el-descriptions-item>
                  <el-descriptions-item label="巡检要求" :span="3">
                    <div style="white-space:pre-wrap; line-height:1.8">{{ processRecord.content || '(无)' }}</div>
                  </el-descriptions-item>
                </el-descriptions>
              </div>
            </el-tab-pane>

            <!-- tab 2+: 巡检过程记录 -->
            <el-tab-pane v-if="!processList.length" name="empty">
              <template #label>
                <span style="display:inline-flex; align-items:center; gap:6px">📝 巡检过程 (0)</span>
              </template>
              <div style="text-align:center; color:#c0c4cc; padding:80px 0">
                暂无巡检过程记录<br />
                <span style="font-size:12px">巡检过程请在小程序端录入</span>
              </div>
            </el-tab-pane>

            <el-tab-pane v-for="(p, idx) in processList" :key="p.id" :name="'p-' + idx">
              <template #label>
                <span style="display:inline-flex; align-items:center; gap:6px">
                  📝
                  <el-tag v-if="Number(p.processResult) === 0" type="danger" size="small" effect="light">异常</el-tag>
                  <el-tag v-else type="success" size="small" effect="light">正常</el-tag>
                  <span>{{ formatHM(p.createTime) }}</span>
                </span>
              </template>
              <div style="padding:20px 24px">
                <div style="font-size:13px; color:#909399; margin-bottom:12px">
                  记录时间：{{ formatDateTime(p.createTime) }}
                  <span v-if="p.locationAddress" style="margin-left:20px">📍 {{ p.locationAddress }}</span>
                  <span v-else-if="p.location" style="margin-left:20px">📍 {{ p.location }}</span>
                </div>
                <div style="font-size:15px; line-height:1.9; white-space:pre-wrap; margin-bottom:14px">
                  {{ p.content || '(无文字描述)' }}
                </div>
                <!-- 图片 -->
                <div v-if="p.images && parseJSON(p.images).length" style="margin-top:10px; display:flex; gap:10px; flex-wrap:wrap">
                  <el-image
                    v-for="(img, i) in parseJSON(p.images)"
                    :key="'i'+i"
                    :src="img"
                    fit="cover"
                    :preview-src-list="parseJSON(p.images)"
                    :initial-index="i"
                    style="width:120px; height:120px; border-radius:4px; cursor:pointer"
                  />
                </div>
                <!-- 视频 -->
                <div v-if="p.videos && parseJSON(p.videos).length" style="margin-top:14px; display:flex; gap:16px; flex-wrap:wrap">
                  <div v-for="(v, i) in parseJSON(p.videos)" :key="'v'+i" class="video-wrap">
                    <video
                      :src="v"
                      class="patrol-video"
                      :style="{ transform: videoRotates['' + p.id + '-' + i] || 'rotate(0deg)' }"
                      controls
                    />
                    <div class="video-rotate-bar">
                      <el-button size="small" link @click="rotateVideo(p.id, i, -90)">↺ 左</el-button>
                      <el-button size="small" link @click="rotateVideo(p.id, i, 90)">↻ 右</el-button>
                      <el-button size="small" link @click="rotateVideo(p.id, i, 0)">⟲ 复位</el-button>
                    </div>
                  </div>
                </div>
              </div>
            </el-tab-pane>
          </el-tabs>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, watch } from 'vue'
import { ElMessage } from 'element-plus'
import { Close } from '@element-plus/icons-vue'
import {
  getPatrolRecordListPage, savePatrolRecord, deletePatrolRecord,
  getPatrolPlanList, savePatrolPlan, deletePatrolPlan,
  getGrainStoreList
} from '../api'
import request from '../utils/request'
import { formatDateTime, formatDate } from '../utils/format'
import MyPager from '../components/MyPager.vue'

// ======== tab & 数据 ========
const activeTab = ref('record')
const patrolRecords = ref([])
const patrolPlans = ref([])
const storeOptions = ref([])
const pageNum = ref(1)
const pageSize = ref(20)
const total = ref(0)
const recordDialogVisible = ref(false)
const planDialogVisible = ref(false)
const processDialogVisible = ref(false)

const storeMap = computed(() => Object.fromEntries(storeOptions.value.map(s => [s.id, s.storeName])))

// ======== 查询条件 ========
const query = reactive({ grainStoreId: null, taskStatus: null, result: null })
const resetQuery = () => { query.grainStoreId = null; query.taskStatus = null; query.result = null; pageNum.value = 1; loadRecords() }

// ======== 每周/每月选项 ========
const weekDays = [
  { v: 1, t: '周一' }, { v: 2, t: '周二' }, { v: 3, t: '周三' },
  { v: 4, t: '周四' }, { v: 5, t: '周五' }, { v: 6, t: '周六' }, { v: 7, t: '周日' }
]
const monthNumbers = Array.from({ length: 28 }, (_, i) => i + 1)
const halfHourTimes = computed(() => {
  const arr = []
  for (let h = 0; h < 24; h++) { arr.push(`${String(h).padStart(2, '0')}:00`); arr.push(`${String(h).padStart(2, '0')}:30`) }
  return arr
})
const weeklyDays = ref([])
const monthlyDays = ref([])

// ======== 表单状态 ========
const recordForm = reactive({
  id: null, recordNo: '', grainStoreId: null, content: '', taskStatus: 0, operator: '', operatorIds: ''
})
const planForm = reactive({
  id: null, planName: '', grainStoreIdsArr: [],
  cycleType: 'once', planTime: null, cycleValue: '',
  executeTime: '08:00', description: '', status: 0
})

// ======== 巡检过程弹窗状态 ========
const processRecord = reactive({ id: null, recordNo: '', grainStoreId: null, content: '', taskStatus: 0, result: 2, operator: '', createTime: null })
const processList = ref([])
const activeProcessTab = ref('detail')  // 默认选中"任务详情" tab
const videoRotates = reactive({})  // key: "processId-index", value: "rotate(90deg)"
const rotateVideo = (pid, idx, delta) => {
  const key = pid + '-' + idx
  const cur = videoRotates[key] || 'rotate(0deg)'
  const match = cur.match(/rotate\((-?\d+)deg\)/)
  let deg = match ? parseInt(match[1], 10) : 0
  if (delta === 0) { deg = 0 } else { deg = (deg + delta) % 360; if (deg < 0) deg += 360 }
  videoRotates[key] = deg === 0 ? 'rotate(0deg)' : `rotate(${deg}deg)`
}

// ======== 加载 ========
onMounted(async () => {
  try {
    const sRes = await getGrainStoreList()
    storeOptions.value = sRes.data || []
  } catch (e) { /* 忽略 */ }
  loadRecords()
  loadPlans()
})
const EXPIRE_MS = 24 * 60 * 60 * 1000
const isExpired = (row) => {
  if (Number(row.taskStatus) === 2) return false
  if (!row.createTime) return false
  return Date.now() - new Date(row.createTime).getTime() > EXPIRE_MS
}

const loadRecords = async () => {
  try {
    const params = { page: pageNum.value, size: pageSize.value }
    if (query.grainStoreId) params.grainStoreId = query.grainStoreId
    if (query.taskStatus === '__expired__') {
      params.expired = true
    } else if (query.taskStatus != null) {
      params.taskStatus = query.taskStatus
    }
    if (query.result) params.result = query.result
    const res = await getPatrolRecordListPage(params)
    patrolRecords.value = res.data?.list || []
    total.value = res.data?.total || 0
  } catch { ElMessage.error('加载巡检任务失败') }
}
const loadPlans = async () => {
  try { patrolPlans.value = (await getPatrolPlanList()).data || [] }
  catch { ElMessage.error('加载巡检计划失败') }
}

// ======== 解析 CSV → 粮库名称数组 ========
const parseStoreNames = (csv) => {
  if (!csv) return []
  return csv.split(',').map(n => storeMap.value[Number(n.trim())]).filter(Boolean)
}

// ======== Record CRUD ========
const genPRNo = () => {
  const d = new Date()
  const pad = (n, w = 2) => String(n).padStart(w, '0')
  const today = `PR${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}`
  let maxSeq = 0
  for (const r of patrolRecords.value) {
    if (r.recordNo && r.recordNo.startsWith(today)) {
      const tail = parseInt(r.recordNo.substring(today.length), 10)
      if (!isNaN(tail) && tail > maxSeq) maxSeq = tail
    }
  }
  return today + pad(maxSeq + 1, 3)
}
const fetchPatrolMember = async (storeId) => {
  if (!storeId) return { ids: '', names: '' }
  try {
    const rows = await request.get('/store-role/list/' + storeId)
    const list = (rows.data || []).filter(r => r.role_type === 'patrol_member')
    const ids = list.map(r => r.user_id).filter(Boolean).join(',')
    const names = list.map(r => r.real_name || r.username).filter(Boolean).join('、')
    return { ids, names }
  } catch (e) { return { ids: '', names: '' } }
}
const onStoreChange = async (storeId) => {
  const m = storeId ? await fetchPatrolMember(storeId) : { ids: '', names: '' }
  recordForm.operator = m.names
  recordForm.operatorIds = m.ids
}

const openRecordModal = async () => {
  Object.assign(recordForm, { id: null, recordNo: genPRNo(), grainStoreId: null, content: '', taskStatus: 0, operator: '', operatorIds: '' })
  recordDialogVisible.value = true
}
const editRecord = async (row) => {
  Object.assign(recordForm, row)
  if (row.grainStoreId) {
    const m = await fetchPatrolMember(row.grainStoreId)
    recordForm.operator = m.names
    recordForm.operatorIds = m.ids
  }
  recordDialogVisible.value = true
}
const deleteRecord = async (id) => {
  try { await deletePatrolRecord(id); ElMessage.success('删除成功'); loadRecords() }
  catch { ElMessage.error('删除失败') }
}
const handleSaveRecord = async () => {
  if (!recordForm.recordNo) return ElMessage.warning('请输入任务编号')
  if (!recordForm.grainStoreId) return ElMessage.warning('请选择粮库')
  if (!recordForm.content) return ElMessage.warning('请输入巡检要求')
  try {
    await savePatrolRecord(recordForm)
    ElMessage.success('保存成功')
    recordDialogVisible.value = false
    loadRecords()
  } catch { ElMessage.error('保存失败') }
}

// ======== 巡检过程弹窗 ========
const openProcess = async (row) => {
  Object.assign(processRecord, row)
  await loadProcesses(row.id)
  activeProcessTab.value = 'detail'   // 默认显示任务详情 tab
  processDialogVisible.value = true
}
const loadProcesses = async (recordId) => {
  try {
    const res = await request.get('/patrol/process/list', { params: { recordId } })
    processList.value = res.data || []
  } catch (e) {
    console.error('loadProcesses 失败:', e)
    processList.value = []
  }
}

// ======== 辅助 ========
const parseJSON = (str) => {
  if (!str) return []
  try {
    const arr = JSON.parse(str)
    return Array.isArray(arr) ? arr : [arr]
  } catch {
    return str.split(',').map(s => s.trim()).filter(Boolean)
  }
}
const formatHM = (iso) => {
  if (!iso) return ''
  const d = new Date(iso)
  const pad = (n) => String(n).padStart(2, '0')
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`
}

// ======== Plan ========
const openPlanModal = () => {
  Object.assign(planForm, {
    id: null, planName: '', grainStoreIdsArr: [],
    cycleType: 'once', planTime: null, cycleValue: '',
    executeTime: '08:00', description: '', status: 0
  })
  weeklyDays.value = []; monthlyDays.value = []
  planDialogVisible.value = true
}
const editPlan = (row) => {
  const ids = (row.grainStoreIds || '').split(',').map(s => Number(s.trim())).filter(n => !isNaN(n))
  Object.assign(planForm, row, { grainStoreIdsArr: ids })
  weeklyDays.value = row.cycleType === 'weekly' && row.cycleValue ? row.cycleValue.split(',').map(Number) : []
  monthlyDays.value = row.cycleType === 'monthly' && row.cycleValue ? row.cycleValue.split(',').map(Number) : []
  planDialogVisible.value = true
}
const deletePlan = async (id) => {
  try { await deletePatrolPlan(id); ElMessage.success('删除成功'); loadPlans() }
  catch { ElMessage.error('删除失败') }
}
watch(() => planForm.cycleType, (t) => {
  if (t !== 'weekly') weeklyDays.value = []
  if (t !== 'monthly') monthlyDays.value = []
})

const handleSavePlan = async () => {
  if (!planForm.planName) return ElMessage.warning('请输入计划名称')
  if (!planForm.grainStoreIdsArr || !planForm.grainStoreIdsArr.length) return ElMessage.warning('请至少选择一个粮库')
  if (!planForm.executeTime) return ElMessage.warning('请选择执行时间')
  const payload = {
    id: planForm.id, planName: planForm.planName,
    grainStoreIds: planForm.grainStoreIdsArr.join(','),
    cycleType: planForm.cycleType, executeTime: planForm.executeTime,
    description: planForm.description, status: planForm.status
  }
  if (planForm.cycleType === 'once') {
    if (!planForm.planTime) return ElMessage.warning('请选择执行日期')
    payload.planTime = planForm.planTime + ' ' + planForm.executeTime + ':00'
    payload.cycleValue = ''
  } else if (planForm.cycleType === 'daily') { payload.planTime = null; payload.cycleValue = '' }
  else if (planForm.cycleType === 'weekly') {
    if (!weeklyDays.value.length) return ElMessage.warning('请选择每周几')
    payload.planTime = null; payload.cycleValue = weeklyDays.value.sort((a, b) => a - b).join(',')
  } else if (planForm.cycleType === 'monthly') {
    if (!monthlyDays.value.length) return ElMessage.warning('请选择每月几号')
    payload.planTime = null; payload.cycleValue = monthlyDays.value.sort((a, b) => a - b).join(',')
  }
  try { await savePatrolPlan(payload); ElMessage.success('保存成功'); planDialogVisible.value = false; loadPlans() }
  catch { ElMessage.error('保存失败') }
}

// ======== 辅助 ========
const weekLabel = (v) => {
  if (!v) return ''
  const map = { 1: '一', 2: '二', 3: '三', 4: '四', 5: '五', 6: '六', 7: '日' }
  return v.split(',').map(n => '周' + map[n.trim()]).join('、')
}
const getPlanStatusName = (s) => ({ 0: '未启用', 1: '执行中', 2: '已完成' }[s] || '未知')
const planStatusTag = (s) => ({ 0: 'info', 1: 'success', 2: 'warning' }[s] || 'info')
</script>

<style scoped>
.toolbar { display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; gap: 12px; flex-wrap: wrap; }
.toolbar .filters { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
.toolbar .actions { display: flex; gap: 8px; }
.lk-text-muted { color: #909399; }

/* ============ 巡检过程覆盖层（和 GrainStore.vue 的 manage 同风格） ============ */
.proc-mask {
  position: fixed;
  left: 240px;
  top: 84px;
  right: 20px;
  bottom: 20px;
  background: rgba(0, 0, 0, 0.45);
  z-index: 999;
}
.proc-panel {
  position: absolute;
  inset: 0;
  background: #fff;
  display: flex;
  flex-direction: column;
  border-radius: 6px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12);
}
.proc-title {
  height: 48px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  font-weight: 600;
  border-bottom: 1px solid #ebeef5;
  font-size: 15px;
}
.proc-tabs {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  padding: 0;
}
.proc-tabs :deep(.el-tabs__content) {
  flex: 1;
  overflow-y: auto;
}
.proc-tabs :deep(.el-tab-pane) {
  height: 100%;
}
.manage-fade-enter-active,
.manage-fade-leave-active {
  transition: opacity 0.2s ease;
}
.manage-fade-enter-from,
.manage-fade-leave-to {
  opacity: 0;
}

/* 巡检过程 - 视频 */
.video-wrap { display: inline-flex; flex-direction: column; gap: 4px; }
.patrol-video { width: 240px; border-radius: 4px; transition: transform 0.25s ease; transform-origin: center center; background: #000; }
.video-rotate-bar { display: flex; justify-content: center; gap: 4px; font-size: 12px; color: #909399; }
</style>
